import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { LoadingBarState } from '@ngx-loading-bar/core/loading-bar.state';
import { Store } from '@ngrx/store';
import { EMPTY, forkJoin, of, timer } from 'rxjs';
import { catchError, concatMap, distinctUntilChanged, exhaustMap, finalize, map, switchMap, tap } from 'rxjs/operators';
import moment from 'moment-timezone';
import { ReportRequest } from '../../../reports/models/report-request';
import { createWorkspaceJob, createWorkspaceJobFromRequest } from '../../../reports/models/report-workspace.helper';
import { RecurringReportsService } from '../../../reports/services/recurring-reports.service';
import { ReportsService } from '../../../reports/services/reports.service';
import * as ReportActions from '../actions/report.actions';
import * as ReportWorkspaceActions from '../actions/report-workspace.actions';
import * as AuthReducers from '../../auth/reducers';

const REPORT_REFRESH_INTERVAL_MS = 60_000;
const RECURRING_REPORT_REFRESH_INTERVAL_MS = 5 * 60_000;

@Injectable()
export class ReportsEffects {

  getRecurringReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.getRecurringReports),
      switchMap(() => this.recurringReportsService.getRecurringReports()
        .pipe(
          exhaustMap((data) => of(ReportActions.getRecurringReportsSuccess({ recurringReports: data }))),
          catchError(error => of(ReportActions.getRecurringReportsFailure({ error })))
        )
      )
    )
  );

  createRecurringReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.createRecurringReport),
      tap(() => this._loader.start()),
      switchMap((action: any) => this.recurringReportsService.createRecurringReports(action['recurringReport'])
        .pipe(
          exhaustMap((data) => of(ReportActions.createRecurringReportSuccess({ recurringReport: data }))),
          catchError(error => of(ReportActions.createRecurringReportFailure({ error }))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  deleteRecurringReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.deleteRecurringReport),
      tap(() => this._loader.start()),
      switchMap((action: any) => this.recurringReportsService.deleteRecurringReports(action['id'])
        .pipe(
          exhaustMap(() => of(ReportActions.deleteRecurringReportSuccess({ id: action['id'] }))),
          catchError(error => of(ReportActions.deleteRecurringReportFailure({ error }))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  /**
   * There is no update endpoint, so an edit replaces the schedule. The replacement is created
   * first: if that fails the original is untouched. The original is removed only afterwards, and
   * if that removal fails the new schedule is kept and reported as created.
   */
  editRecurringReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.editRecurringReport),
      tap(() => this._loader.start()),
      exhaustMap((action: any) =>
        this.recurringReportsService.createRecurringReports(action.recurringReport).pipe(
          concatMap((created) => this.recurringReportsService.deleteRecurringReports(action.oldId).pipe(
            map(() => ReportActions.editRecurringReportSuccess({ oldId: action.oldId, recurringReport: created })),
            catchError(error => of(
              ReportActions.createRecurringReportSuccess({ recurringReport: created }),
              ReportActions.deleteRecurringReportFailure({ error }),
            )),
          )),
          catchError(error => of(ReportActions.editRecurringReportFailure({ error }))),
          finalize(() => this._loader.complete()),
        )
      )
    )
  );

  /**
   * The create response has no server-computed fields such as the next run, so the list is read
   * again after a schedule is created or replaced.
   */
  refreshSchedulesAfterSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.createRecurringReportSuccess, ReportActions.editRecurringReportSuccess),
      map(() => ReportActions.getRecurringReports())
    )
  );

  schedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.schedule),
      tap(() => this._loader.start()),
      switchMap((action: any) => this.reportsService.schedule(action['reportParams'])
        .pipe(
          concatMap((result) => {
            const followUpActions: any[] = [ReportActions.scheduleSuccess({ result })];

            if (result?.generationAttemptId) {
              followUpActions.push(
                ReportWorkspaceActions.upsertReportRequest({
                  job: createWorkspaceJob(action['reportParams'], result.generationAttemptId)
                }),
                ReportWorkspaceActions.refreshReportRequest({ generationAttemptId: result.generationAttemptId })
              );
            }

            return of(...followUpActions);
          }),
          catchError(error => of(ReportActions.scheduleFailure({ error }))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  refreshReportRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportWorkspaceActions.refreshReportRequests),
      switchMap(() => this.reportsService.getReportRequests(this._buildRequestParams())
        .pipe(
          map((data) => (data || []).map(reportRequest => createWorkspaceJobFromRequest(new ReportRequest(reportRequest)))),
          map((jobs) => ReportWorkspaceActions.setReportRequests({ jobs })),
          catchError(() => EMPTY)
        )
      )
    )
  );

  refreshReportRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportWorkspaceActions.refreshReportRequest),
      switchMap((action) => this.reportsService.getReportRequest(action.generationAttemptId)
        .pipe(
          map((data) => createWorkspaceJobFromRequest(new ReportRequest(data))),
          map((job) => ReportWorkspaceActions.upsertReportRequest({ job })),
          catchError(() => EMPTY)
        )
      )
    )
  );

  retryReportRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportWorkspaceActions.retryReportRequest),
      switchMap((action) => this.reportsService.retryReportRequest(action.generationAttemptId)
        .pipe(
          concatMap((result) => of(
            ReportWorkspaceActions.retryReportRequestSuccess({
              generationAttemptId: action.generationAttemptId,
              newGenerationAttemptId: result?.generationAttemptId
            }),
            // Pull the new run straight away so the retry appears without waiting for the
            // next poll tick, and refresh the list so the retried row updates too.
            ReportWorkspaceActions.refreshReportRequest({ generationAttemptId: result?.generationAttemptId }),
            ReportWorkspaceActions.refreshReportRequests()
          )),
          catchError(error => of(ReportWorkspaceActions.retryReportRequestFailure({
            generationAttemptId: action.generationAttemptId,
            error
          })))
        )
      )
    )
  );

  loadRecurringReportRuns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportWorkspaceActions.loadRecurringReportRuns),
      switchMap((action) => this.recurringReportsService.getRecurringReportRuns(action.recurringReportId)
        .pipe(
          map((data) => ReportWorkspaceActions.loadRecurringReportRunsSuccess({
            recurringReportId: action.recurringReportId,
            jobs: (data || []).map(reportRequest => createWorkspaceJobFromRequest(new ReportRequest(reportRequest)))
          })),
          catchError(error => of(ReportWorkspaceActions.loadRecurringReportRunsFailure({
            recurringReportId: action.recurringReportId,
            error
          })))
        )
      )
    )
  );

  getReportsHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.getReportsHistory),
      switchMap((action: any) => this.reportsService.getReportsHistory(action.params)
        .pipe(
          map((data) => ReportActions.getReportsHistorySuccess({ reports: data || [] })),
          catchError(error => of(ReportActions.getReportsHistoryFailure({ error }))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  reportWorkspacePolling$ = createEffect(() =>
    this.store$.select(AuthReducers.getLoggedIn).pipe(
      distinctUntilChanged(),
      switchMap(loggedIn => loggedIn
        ? timer(0, REPORT_REFRESH_INTERVAL_MS).pipe(
          concatMap(() => of(
            ReportWorkspaceActions.refreshReportRequests(),
            ReportActions.getReportsHistory({ params: this._buildHistoryParams() })
          ))
        )
        : EMPTY
      )
    )
  );

  recurringReportsPolling$ = createEffect(() =>
    this.store$.select(AuthReducers.getLoggedIn).pipe(
      distinctUntilChanged(),
      switchMap(loggedIn => loggedIn
        ? timer(0, RECURRING_REPORT_REFRESH_INTERVAL_MS).pipe(
          map(() => ReportActions.getRecurringReports())
        )
        : EMPTY
      )
    )
  );

  startWorkspacePolling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportWorkspaceActions.startWorkspacePolling),
      concatMap(() => of(
        ReportWorkspaceActions.refreshReportRequests(),
        ReportActions.getRecurringReports(),
        ReportActions.getReportsHistory({ params: this._buildHistoryParams() })
      ))
    )
  );

  private _loader: LoadingBarState;

  constructor(
    private actions$: Actions,
    private store$: Store<any>,
    private recurringReportsService: RecurringReportsService,
    private reportsService: ReportsService,
    private _loadingBar: LoadingBarService
  ) {
    this._loader = this._loadingBar.useRef('main');
  }

  private _buildHistoryParams() {
    return {
      createdAfter: moment().subtract(30, 'd').startOf('d').utc().format(),
      createdBefore: moment().add(1, 'm').utc().format(),
      page: 1,
      pageSize: 25,
      sorts: '-createdAt'
    };
  }

  private _buildRequestParams() {
    return {
      page: 1,
      pageSize: 25,
      sorts: '-queuedAt'
    };
  }
}
